/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/no-runloop, ember/require-computed-property-dependencies, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
// Ember Imports
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { computed } from '@ember/object';
import { addObserver, removeObserver } from '@ember/object/observers';
import { service } from '@ember/service';
import { scheduleOnce } from '@ember/runloop';
import { byKeys } from '@linc-technologies/linced-common/utils/arrays';
import { currentRubricProgressions } from '@linc-technologies/linced-common/utils/rubrics';
// D3
import { select } from 'd3-selection';
import {
  attributeBindings,
  classNames,
  tagName,
} from '@ember-decorators/component';

const TOTAL_W = 412;
const SAMPLE_GRADE_LABELS = [
  'Emerging',
  'Developing',
  'Consolidating',
  'Proficient',
  'Exceeding',
];
const PAD = 8;
const BAR_H = 15;
const BAR_R = 4;
const MARKER_R = 10;
const PAD_TOP = 15; // y of first row's name centre
const BAR_FROM_NAME = 12; // bar top y-offset from name centre
const GRADE_FROM_NAME = 38; // grade label baseline y-offset from name centre
const NAME_FONT = 8;
const GRADE_FONT = 7;
const NAME_FONT_MOBILE = 16;
const GRADE_FONT_MOBILE = 12;
const MOBILE_BREAKPOINT = 480;
const BOTTOM_MARGIN = 10; // breathing room below the last strand's grade labels

@tagName('svg')
@classNames('chart', 'strand-slider-visual')
@attributeBindings('preserveAspectRatio', 'viewBox')
export default class StrandSliderVisual extends Component {
  preserveAspectRatio = 'xMinYMin meet';

  @service
  dates;

  @service
  store;

  // Passed in
  section = null;

  @tracked subject = null;
  @tracked theme = 'blue';
  @tracked mode = 'template';
  student = null;
  studentSection = null;
  viewBox = null;
  showBarBorders = false;

  themeColorMap = Object.freeze({
    teal: {
      containerBackground: 'rgba(220, 235, 235, 1)',
      segmentFills: ['#9ccfd3', '#b7dde1', '#cfe8ea', '#e1eff0', '#edf5f5'],
      sliderChipFill: '#0b335c',
    },
    blue: {
      containerBackground: 'rgba(218, 232, 236, 0.38)',
      segmentFills: ['#04667cb3', '#78A9B4', '#8FB6BF', '#A6C4CA', '#BED1D6'],
      sliderChipFill: '#055060',
    },
    warm: {
      containerBackground: 'rgba(254, 243, 199, 1)',
      segmentFills: ['#fde68a', '#fef08a', '#fef9c3', '#fef5d3', '#fffbeb'],
      sliderChipFill: '#92400e',
    },
    monochrome: {
      containerBackground: '#F4F4F4',
      segmentFills: ['#9ca3af', '#d1d5db', '#e5e7eb', '#f3f4f6', '#f9fafb'],
      sliderChipFill: '#1f2937',
    },
  });

  // TODO: replace with real subject strands
  sampleStrands = Object.freeze(['Strand 1', 'Strand 2', 'Strand 3']);

  @computed('theme')
  get themeColors() {
    return this.themeColorMap[this.theme] || this.themeColorMap.teal;
  }

  @computed('subject.possibleGrades.[]')
  get demoActiveIndex() {
    let grades = this.get('subject.possibleGrades') || [];
    let n = grades.length || SAMPLE_GRADE_LABELS.length;
    return Math.floor((n - 1) / 2);
  }

  // One row per strand: { name, markerIndex (0..n-1 or null) }
  @computed(
    'mode',
    'subject',
    'subject.possibleGrades.[]',
    'studentSection.elementProgressions.@each.{elementId,judgement}',
    'student',
    'dates.rolloverMonthDate',
    'section.configuration',
    'themeColors',
    'demoActiveIndex',
  )
  get strandRows() {
    let grades = this.get('subject.possibleGrades') || [];

    if (this.mode === 'report') {
      let elementProgressions = currentRubricProgressions(
        this.get('studentSection.elementProgressions'),
        {
          store: this.store,
          subject: this.subject,
          student: this.student,
          rolloverMonthDate: this.dates.rolloverMonthDate,
          templateSection: this.section,
        },
      );

      let rows = elementProgressions
        .map((entry) => {
          let elementId = entry.get('elementId');
          let element = this.store.peekRecord(
            'reports/rubric-element',
            elementId,
          );
          let raw = entry.get('judgement');
          let isNA = raw === 'na';
          let judgement = !isNA && raw ? parseInt(raw, 10) : null;
          let markerIndex = null;
          if (grades.length && judgement != null) {
            let idx = grades.findIndex((g) => g.value === judgement);
            markerIndex = idx >= 0 ? idx : null;
          }
          return {
            name: element ? element.get('name') : null,
            sequence: element ? element.get('sequence') : 0,
            markerIndex,
            isNA,
          };
        })
        .filter((row) => row.name)
        .sort(byKeys('sequence'));

      if (rows.length) {
        return rows;
      }
    }

    // Template mode (or report mode with no element data) — sample strands
    let markerIndex = this.demoActiveIndex;
    return this.sampleStrands.map((name) => ({
      name,
      markerIndex,
      isNA: false,
    }));
  }

  didInsertElement() {
    super.didInsertElement(...arguments);
    addObserver(this, 'strandRows', this, 'onStrandRowsChange');
    scheduleOnce('render', this, this.draw);
  }

  willDestroyElement() {
    super.willDestroyElement(...arguments);
    removeObserver(this, 'strandRows', this, 'onStrandRowsChange');
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    scheduleOnce('render', this, this.draw);
  }

  onStrandRowsChange() {
    scheduleOnce('render', this, this.draw);
  }

  draw() {
    let el = this.element;
    if (!el) {
      return;
    }

    let rows = this.strandRows || [];
    let themeColors = this.themeColors || {};
    let grades = this.get('subject.possibleGrades') || [];

    let nGrades = grades.length || 5;
    let nRows = rows.length;
    if (!nRows) {
      return;
    }

    let totalW = TOTAL_W;
    let barW = totalW - 2 * PAD;
    let segW = barW / nGrades;

    // Resolve font sizes before layout so dynamic slot height can use them.
    let containerWidth =
      (el.parentElement && el.parentElement.getBoundingClientRect().width) ||
      window.innerWidth ||
      TOTAL_W;
    let isMobile = containerWidth < MOBILE_BREAKPOINT;
    let nameFontSize = isMobile ? NAME_FONT_MOBILE : NAME_FONT;
    let gradeFontSize = isMobile ? GRADE_FONT_MOBILE : GRADE_FONT;

    // Pre-compute wrapped grade labels to derive dynamic row spacing.
    let labelLineH = gradeFontSize + 1;
    let maxCharsPerLabel = Math.max(
      4,
      Math.floor(segW / (gradeFontSize * 0.62)),
    );
    let gradeLabels = Array.from({ length: nGrades }, (_, si) => {
      let gs = grades[si] ? grades[si].string : SAMPLE_GRADE_LABELS[si] || '';
      return ssvWrapText(gs, maxCharsPerLabel);
    });
    let maxLabelLines = gradeLabels.reduce((m, l) => Math.max(m, l.length), 1);

    // Section border geometry — consistent across all rows.
    let sectionPadTop = nameFontSize + 2;
    let sectionPadBottom = 2;
    let sectionH =
      sectionPadTop +
      GRADE_FROM_NAME +
      (maxLabelLines - 1) * labelLineH +
      sectionPadBottom;

    // isNA rows skip grade labels so they get a compact slot height.
    let fullSlotH = sectionH + 2;
    let isNASlotH =
      sectionPadTop + BAR_FROM_NAME + BAR_H + sectionPadBottom + 2;

    // Per-row name-centre Y positions (rows may have different slot heights).
    let nameYOffsets = [];
    let lastIsNA = false;
    let _runY = PAD_TOP;
    rows.forEach((row) => {
      nameYOffsets.push(_runY);
      lastIsNA = row.isNA;
      _runY += row.isNA ? isNASlotH : fullSlotH;
    });
    let totalH =
      nameYOffsets[nRows - 1] +
      (lastIsNA
        ? BAR_FROM_NAME + BAR_H + sectionPadBottom + 2
        : sectionH - sectionPadTop + 2) +
      BOTTOM_MARGIN;

    if (!this.viewBox) {
      el.setAttribute('viewBox', `0 0 ${totalW} ${totalH}`);
    }
    el.setAttribute('width', '100%');
    el.removeAttribute('height');

    let svg = select(el);
    svg.selectAll('*').remove();

    let { containerBackground, segmentFills, sliderChipFill } = themeColors;

    // --- Container background ---
    svg
      .append('rect')
      .attr('width', totalW)
      .attr('height', totalH)
      .attr('fill', containerBackground || '#dce9e9');

    // --- Defs: clip paths for bars + grade label columns ---
    let defs = svg.append('defs');
    let pfx = this.elementId;
    let labelClipH = maxLabelLines * labelLineH + gradeFontSize;

    rows.forEach((row, ri) => {
      let nameY = nameYOffsets[ri];
      let barY = nameY + BAR_FROM_NAME;

      // Bar clip path
      defs
        .append('clipPath')
        .attr('id', `${pfx}-ssv-bar-${ri}`)
        .append('rect')
        .attr('x', PAD)
        .attr('y', barY)
        .attr('width', barW)
        .attr('height', BAR_H)
        .attr('rx', BAR_R)
        .attr('ry', BAR_R);

      // Per-segment label clip paths (prevents text bleeding across columns)
      let gradeLabelY = nameY + GRADE_FROM_NAME;
      for (let si = 0; si < nGrades; si++) {
        defs
          .append('clipPath')
          .attr('id', `${pfx}-ssv-lbl-${ri}-${si}`)
          .append('rect')
          .attr('x', PAD + si * segW)
          .attr('y', gradeLabelY - labelLineH)
          .attr('width', segW)
          .attr('height', labelClipH);
      }
    });

    // --- Rows ---
    rows.forEach((row, ri) => {
      let nameY = nameYOffsets[ri];
      let barY = nameY + BAR_FROM_NAME;
      let markerCY = barY + BAR_H / 2;
      let gradeLabelY = nameY + GRADE_FROM_NAME;

      let rowG = svg.append('g');

      // 1. Strand name
      rowG
        .append('text')
        .attr('x', PAD)
        .attr('y', nameY)
        .attr('dy', '0.35em')
        .attr('fill', '#111')
        .attr('font-size', nameFontSize)
        .attr('font-weight', '700')
        .attr('font-family', 'sans-serif')
        .text(row.name);

      // 2. Bar background rect
      rowG
        .append('rect')
        .attr('x', PAD)
        .attr('y', barY)
        .attr('width', barW)
        .attr('height', BAR_H)
        .attr('rx', BAR_R)
        .attr('ry', BAR_R)
        .attr('fill', '#ececec');

      if (row.isNA) {
        // Skip segment fills, grade labels and marker. Show a single
        // "Not yet taught" message centred on the bar.
        rowG
          .append('text')
          .attr('x', PAD + barW / 2)
          .attr('y', markerCY)
          .attr('dy', '0.35em')
          .attr('fill', 'rgba(0,0,0,0.65)')
          .attr('font-size', gradeFontSize)
          .attr('font-style', 'italic')
          .attr('font-family', 'sans-serif')
          .attr('text-anchor', 'middle')
          .text('Not yet taught');
        return;
      }

      // 3 & 4. Clipped segment fills
      let segG = rowG
        .append('g')
        .attr('clip-path', `url(#${pfx}-ssv-bar-${ri})`);

      let fills = segmentFills || [];
      for (let si = 0; si < nGrades; si++) {
        segG
          .append('rect')
          .attr('x', PAD + si * segW)
          .attr('y', barY)
          .attr('width', segW)
          .attr('height', BAR_H)
          .attr('fill', fills[si % fills.length] || '#ccc');
      }

      if (this.showBarBorders) {
        // 4b. Segment dividers — clipped to bar shape so they respect rounded corners
        let divG = rowG
          .append('g')
          .attr('clip-path', `url(#${pfx}-ssv-bar-${ri})`);
        for (let si = 1; si < nGrades; si++) {
          let divX = PAD + si * segW;
          divG
            .append('line')
            .attr('x1', divX)
            .attr('y1', barY)
            .attr('x2', divX)
            .attr('y2', barY + BAR_H)
            .attr('stroke', sliderChipFill || '#0b335c')
            .attr('stroke-opacity', 0.35)
            .attr('stroke-width', 0.4);
        }

        // 4c. Bar outline
        rowG
          .append('rect')
          .attr('x', PAD)
          .attr('y', barY)
          .attr('width', barW)
          .attr('height', BAR_H)
          .attr('rx', BAR_R)
          .attr('ry', BAR_R)
          .attr('fill', 'none')
          .attr('stroke', sliderChipFill || '#0b335c')
          .attr('stroke-width', 0.4);
      }

      // 5. Grade labels — wrapped tspans, clipped to their segment column
      for (let si = 0; si < nGrades; si++) {
        let lines = gradeLabels[si];
        let midX = PAD + si * segW + segW / 2;

        let textEl = rowG
          .append('text')
          .attr('fill', 'rgba(0,0,0,0.65)')
          .attr('font-size', gradeFontSize)
          .attr('font-family', 'sans-serif')
          .attr('text-anchor', 'middle')
          .attr('clip-path', `url(#${pfx}-ssv-lbl-${ri}-${si})`);

        lines.forEach((line, li) => {
          textEl
            .append('tspan')
            .attr('x', midX)
            .attr('y', gradeLabelY + li * labelLineH)
            .attr('dy', '0.35em')
            .text(line);
        });
      }

      // 6. Marker circle (only when markerIndex is not null)
      if (row.markerIndex != null) {
        let markerCX = PAD + row.markerIndex * segW + segW / 2;
        rowG
          .append('circle')
          .attr('cx', markerCX)
          .attr('cy', markerCY)
          .attr('r', MARKER_R)
          .attr('fill', sliderChipFill || '#0b335c');
      }
    });
  }
}

function ssvWrapText(label, maxCharsPerLine) {
  let words = label.split(' ');
  let lines = [];
  let current = '';
  words.forEach((word) => {
    let test = current ? `${current} ${word}` : word;
    if (test.length > maxCharsPerLine && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  });
  if (current) {
    lines.push(current);
  }
  return lines;
}
